// Request 116 (UM_GET_XML, mxMessageBase.UM_BASE+16) — mensagem do ucMain/ucGEDMain, não de uma UC
// de tela. Devolve a árvore de menu do sistema. Confirmado ao vivo em 2026-08-27 (Fase 0 do plano de
// implementação): `ucuGEDMain.GetGUIXML` lê um resource compilado (`mmenu`, RT_RCDATA) e o resultado
// passa por `acSecurityManager.Instance.ApplySecurity` no framework (`ucuMain.pas`, branch genérico —
// SystemCode do GED é '61', não cai no branch hardcoded do SISEG) antes de voltar pro frontend — o
// menu que chega aqui já vem filtrado pela permissão do usuário logado. `session.sendRequest(116,
// {...})` direto (sem UseCaseManager) — Session extends MainUseCase extends UseCase, que tem
// sendRequest herdado.
export const SYSTEM_MENU_RM = 116;

// _Type: MI = grupo (Menu Item), MS = sub-item ligado a um buscador (Entidade, `_EN`), MU = ação/caso
// de uso concreto (o que de fato abre uma UC).
//
// Confirmado ao vivo (2026-08-27, resposta real capturada via console): `_UI` NÃO identifica a tela de
// forma única entre operações da mesma entidade — ex. Tipo de Documento Incluir/Alterar/Excluir têm os
// três `_UI: "885"`, apesar de serem 3 rotas React distintas em `routes.ts`. Um mesmo `MI` "Alterar"
// pode ainda agrupar vários `Actions` com `_UI` diferentes entre si (ex. "Alterar" de Empréstimo tem 3
// actions — Alterar Empréstimo/Alterar Devolução/Desfazer Devolução — cada uma com `_UI` próprio).
// `_UC` (caso de uso) + `_OP` (operação) juntos são a chave estável pra mapear rota — ver
// `getRouteCandidates` e `ROUTE_MAP` em `menuTree.ts`.
export interface SystemMenuAction {
  _Type: "MU";
  _Name: string;
  _UC: string;
  _OP: string;
  _UI: string;
  _IC?: string;
  /** Presente em alguns MU direto (ex. "Empresa", "Incluir" de Tipo Documento) — hint de renderização, não usado aqui. */
  _VIEW?: string;
}

export interface SystemMenuNode {
  _Type: "MI" | "MS" | "MU";
  _Name: string;
  _UC?: string;
  _OP?: string;
  _UI?: string;
  _EN?: string;
  _IC?: string;
  _VIEW?: string;
  Submenus?: SystemMenuNode[];
  Actions?: SystemMenuAction[];
}

export interface SystemMenuResponse {
  System: {
    _Name: string;
    Menus: SystemMenuNode[];
  };
}

/** Chave estável de roteamento — ver nota acima sobre por que não é só `_UI`. */
export const routeKey = (uc: string, op: string): string => `${uc}:${op}`;

export interface RouteCandidate {
  key: string;
  op: string;
}

// Candidatas de rota pra este nó, em ordem de preferência — um MU direto tem uma única candidata
// (a dele mesmo); um MS pode empacotar VÁRIOS Actions sob um único rótulo clicável (ex. "Alterar" de
// Empréstimo), cada um com seu próprio par _UC/_OP — buildNode tenta cada uma em ordem até achar uma
// com rota mapeada em ROUTE_MAP, já que todas levam ao mesmo clique de menu.
export const getRouteCandidates = (node: SystemMenuNode): RouteCandidate[] => {
  if (node._UC && node._OP) return [{ key: routeKey(node._UC, node._OP), op: node._OP }];
  return (node.Actions ?? [])
    .filter((action) => action._UC && action._OP)
    .map((action) => ({ key: routeKey(action._UC, action._OP), op: action._OP }));
};

// Todas as chaves `_UC:_OP` presentes na árvore CRUA (antes de ROUTE_MAP/buildMenuTree) — de todo MU
// direto ou Action dentro de um MS, em qualquer profundidade. Independente de a tela ter rota própria
// mapeada: usado pra permissão de AÇÃO dentro de uma tela já aberta (ver useAllowedActionKeys), não só
// pra decidir se um item aparece no menu lateral.
export const flattenActionKeys = (menus: SystemMenuNode[]): Set<string> => {
  const keys = new Set<string>();
  const visit = (nodes: SystemMenuNode[]) => {
    for (const node of nodes) {
      if (node._UC && node._OP) keys.add(routeKey(node._UC, node._OP));
      for (const action of node.Actions ?? []) {
        if (action._UC && action._OP) keys.add(routeKey(action._UC, action._OP));
      }
      if (node.Submenus) visit(node.Submenus);
    }
  };
  visit(menus);
  return keys;
};
